class CartNotificationUpsell extends HTMLElement {
  connectedCallback() {
    // Watch #cart-notification-product for new content — fires each time an
    // item is added to cart and the notification renders the product row.
    const productEl = document.getElementById('cart-notification-product');
    if (!productEl) return;

    this.observer = new MutationObserver(() => this.onProductRendered());
    this.observer.observe(productEl, { childList: true });
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
  }

  async onProductRendered() {
    // Read the product ID stamped onto the cart item div by the section.
    const cartItemEl = document
      .getElementById('cart-notification-product')
      ?.querySelector('[data-product-id]');

    if (!cartItemEl) return;

    const productId = cartItemEl.dataset.productId;

    // Same product re-added — recommendations don't change, skip the fetch.
    if (productId === this._lastProductId) return;
    this._lastProductId = productId;

    this.innerHTML = '';

    try {
      // Fetch cart state and recommendations in parallel.
      const [cart, recsData] = await Promise.all([
        fetch('/cart.js').then((r) => r.json()),
        fetch(
          `/recommendations/products.json?product_id=${productId}&limit=8&intent=related`
        ).then((r) => r.json()),
      ]);

      const cartProductIds = new Set(cart.items.map((i) => i.product_id));
      const recs = recsData.products
        .filter((p) => !cartProductIds.has(p.id))
        .slice(0, 4);

      if (recs.length === 0) return;

      this.render(recs);
      this.bindEvents();
    } catch (_e) {
      // Fail silently — upsell is non-critical.
    }
  }

  render(products) {
    this.innerHTML = `
      <div class="cart-notification-upsell">
        <p class="cart-notification-upsell__heading">You might also like</p>
        <ul class="cart-notification-upsell__list" role="list">
          ${products.map((p) => this.renderCard(p)).join('')}
        </ul>
      </div>
    `;
  }

  renderCard(product) {
    const variant = product.variants[0];
    const price = this.formatMoney(product.price_min);
    const hasOptions = product.variants.length > 1;
    const available = variant.available;

    const imageHtml = product.featured_image
      ? `<img
          src="${this.resizeImage(product.featured_image, '200x200')}"
          alt="${this.escHtml(product.title)}"
          width="96"
          height="96"
          loading="lazy"
        >`
      : '';

    let actionHtml;
    if (hasOptions) {
      actionHtml = `<a href="${product.url}" class="cart-notification-upsell-card__btn">View</a>`;
    } else if (!available) {
      actionHtml = `<span class="cart-notification-upsell-card__sold-out">Sold out</span>`;
    } else {
      actionHtml = `<button
        class="cart-notification-upsell-card__btn"
        type="button"
        data-variant-id="${variant.id}"
        aria-label="Add ${this.escHtml(product.title)} to cart"
      >
        <span class="btn-text">Add</span>
        <svg class="btn-spinner spinner" aria-hidden="true" focusable="false" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
          <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
        </svg>
      </button>`;
    }

    return `
      <li class="cart-notification-upsell-card">
        <a href="${product.url}" class="cart-notification-upsell-card__image-link" tabindex="-1" aria-hidden="true">
          ${imageHtml}
        </a>
        <a href="${product.url}" class="cart-notification-upsell-card__title h5">${this.escHtml(product.title)}</a>
        <span class="cart-notification-upsell-card__price">${price}</span>
        ${actionHtml}
      </li>
    `;
  }

  bindEvents() {
    this.querySelector('.cart-notification-upsell__list').addEventListener('click', (e) => {
      const btn = e.target.closest('[data-variant-id]');
      if (btn) {
        e.preventDefault();
        this.addToCart(btn.dataset.variantId, btn);
      }
    });
  }

  async addToCart(variantId, button) {
    button.disabled = true;
    button.classList.add('loading');

    try {
      const response = await fetch(`${window.routes.cart_add_url}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          id: Number(variantId),
          quantity: 1,
          properties: { '_Source': 'Cart Notification Upsell' },
          sections: ['cart-notification-product', 'cart-notification-button', 'cart-icon-bubble'],
        }),
      });

      const data = await response.json();

      if (data.sections) {
        const productEl = document.getElementById('cart-notification-product');
        const buttonEl = document.getElementById('cart-notification-button');
        const bubbleEl = document.getElementById('cart-icon-bubble');

        // Refresh the product row — this also fires the MutationObserver,
        // which will update the recommendations for the newly added product.
        if (productEl && data.sections['cart-notification-product']) {
          const parsed = new DOMParser().parseFromString(
            data.sections['cart-notification-product'],
            'text/html'
          );
          const fresh = parsed.querySelector(`[id="cart-notification-product-${data.key}"]`);
          if (fresh) productEl.innerHTML = fresh.outerHTML;
        }

        if (buttonEl && data.sections['cart-notification-button']) {
          const parsed = new DOMParser().parseFromString(
            data.sections['cart-notification-button'],
            'text/html'
          );
          const fresh = parsed.querySelector('.shopify-section');
          if (fresh) buttonEl.innerHTML = fresh.innerHTML;
        }

        if (bubbleEl && data.sections['cart-icon-bubble']) {
          const parsed = new DOMParser().parseFromString(
            data.sections['cart-icon-bubble'],
            'text/html'
          );
          const fresh = parsed.querySelector('.shopify-section');
          if (fresh) bubbleEl.innerHTML = fresh.innerHTML;
        }
      }
    } catch (_e) {
      button.disabled = false;
      button.classList.remove('loading');
    }
  }

  formatMoney(cents) {
    const format = window.cartUpsellMoneyFormat || '${{amount}}';

    function formatWithDelimiters(number, precision, thousands, decimal) {
      const value = (number / 100).toFixed(precision);
      const parts = value.split('.');
      const dollars = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
      const centsPart = precision > 0 && parts[1] ? decimal + parts[1] : '';
      return dollars + centsPart;
    }

    const match = format.match(/\{\{\s*(\w+)\s*\}\}/);
    if (!match) return format;

    let value;
    switch (match[1]) {
      case 'amount':
        value = formatWithDelimiters(cents, 2, ',', '.');
        break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        value = formatWithDelimiters(cents, 2, "'", '.');
        break;
      case 'amount_with_space_separator':
        value = formatWithDelimiters(cents, 2, ' ', ',');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = formatWithDelimiters(cents, 0, ' ', ',');
        break;
      default:
        value = formatWithDelimiters(cents, 2, ',', '.');
    }

    return format.replace(match[0], value);
  }

  resizeImage(url, size) {
    if (!url) return '';
    return url.replace(/(\.[^.?]+)(\?.*)?$/, `_${size}$1$2`);
  }

  escHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }
}

customElements.define('cart-notification-upsell', CartNotificationUpsell);
